import { useState, FormEvent, useMemo } from "react";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { DEFAULT_AXNUI_CONTENT } from "@iddataweb/idw-common-ui/constants";
import { LocaleTypes } from "@iddataweb/idw-common-ui/enums";
import { resources } from "@constants/hardcoded-strings";
import { ButtonTypes } from "@enums/button-types";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { ModalSizeTypes } from "@enums/modal-size-types";
import { FormattedRpServiceConfigurationAttribute } from "@idw-types/data/rp-service-configuration";
import { SelectOption } from "@idw-types/common";
import { getDefaultAttributeLabel } from "@utils/attribute-label-util";
import { sortStrings } from "@utils/string-util";
import { DefaultModal } from "../../common/modals/default-modal/default-modal";
import { SelectFormGroup } from "../../common/select-form-group/select-form-group";

type AddRpServiceConfigurationAttributeModalProps = {
    onModalClose: () => void;
    isUsingThemedUI: boolean;
    maxOrder: number;
    canModifyBranding: boolean;
    rpServiceConfigurationAttributes: Array<FormattedRpServiceConfigurationAttribute>;
    addRpServiceConfigurationAttribute: (
        rpServiceConfigurationAttribute: FormattedRpServiceConfigurationAttribute,
        labelForTheming: string
    ) => void;
};

type AddAttributeLabelModalState = {
    rpServiceConfigurationAttribute: FormattedRpServiceConfigurationAttribute;
    selectedRpServiceConfigurationAttributeOption: SelectOption;
    selectedAttributeLabel: SelectOption;
};

export const AddRpServiceConfigurationAttributeModal = (props: AddRpServiceConfigurationAttributeModalProps) => {
    const {
        onModalClose,
        isUsingThemedUI,
        maxOrder,
        canModifyBranding,
        rpServiceConfigurationAttributes,
        addRpServiceConfigurationAttribute
    } = props;

    const [labelWidth] = useState(LabelWidthTypes.SMALL);
    const [state, setState] = useState<AddAttributeLabelModalState>({
        rpServiceConfigurationAttribute: null,
        selectedRpServiceConfigurationAttributeOption: null,
        selectedAttributeLabel: null
    });

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();

        let attributeLabel = null;
        // if using themed ui and attribute label not defined- set the attribute label to the default value
        if (!state.selectedAttributeLabel && isUsingThemedUI) {
            attributeLabel = getDefaultAttributeLabel(
                state.rpServiceConfigurationAttribute.attributeType,
                state.rpServiceConfigurationAttribute.attributeLabels,
                DEFAULT_AXNUI_CONTENT[LocaleTypes.ENGLISH]
            );
        } else if (state.selectedAttributeLabel) {
            attributeLabel = state.rpServiceConfigurationAttribute.attributeLabels.find(al => al.id === state.selectedAttributeLabel.value);
        }

        addRpServiceConfigurationAttribute(
            {
                ...state.rpServiceConfigurationAttribute,
                attributeLabel: attributeLabel,
                order: maxOrder,
                status: EntityStatusTypes.ACTIVE,
                addedToService: true
            },
            state.selectedAttributeLabel?.label,
        );
        onModalClose();
    };

    const handleAttributeChange = (newValue: SelectOption) => {
        const rpServiceConfigurationAttribute = rpServiceConfigurationAttributes.find(ap => ap.uuid === newValue.value);

        if (rpServiceConfigurationAttribute) {
            const attributeLabel = getDefaultAttributeLabel(
                rpServiceConfigurationAttribute.attributeType,
                rpServiceConfigurationAttribute.attributeLabels,
                DEFAULT_AXNUI_CONTENT[LocaleTypes.ENGLISH]
            );

            const attributeLabelSelectOption: SelectOption = attributeLabel ? {
                label: attributeLabel.label,
                value: attributeLabel.id
            } : null;

            setState({
                rpServiceConfigurationAttribute: rpServiceConfigurationAttribute,
                selectedRpServiceConfigurationAttributeOption: newValue,
                selectedAttributeLabel: attributeLabelSelectOption
            });
        }
    };

    const handleAttributeLabelChange = (newValue: SelectOption) => {
        setState({
            ...state,
            selectedAttributeLabel: newValue
        });
    };

    const attributeOptions = useMemo(() => {
        return rpServiceConfigurationAttributes
            .map(attr => ({ label: attr.name, value: attr.uuid }))
            .sort((a, b) => sortStrings(a.label, b.label));
    }, [rpServiceConfigurationAttributes]);

    const attributeLabelOptions = useMemo(() => {
        if (state.rpServiceConfigurationAttribute) {
            return state.rpServiceConfigurationAttribute.attributeLabels.map(al => ({ label: al.label, value: al.id })).sort((a, b) => sortStrings(a.label, b.label));
        }
        return [];
    }, [state.rpServiceConfigurationAttribute]);

    return (
        <DefaultModal
            automationId="add-attribute-modal"
            ariaLabelledBy="add-attribute-title"
            onHide={onModalClose}
            backdrop={"static"}
            keyboard={false}
            isMaxWidth={false}
            size={ModalSizeTypes.MEDIUM}
        >
            <Form onSubmit={handleSubmit}>
                <Modal.Header id={"add-attribute-title"} closeButton>
                    <Modal.Title>{resources.modals.addRpServiceConfigurationAttribute.title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <SelectFormGroup
                        controlId={"attribute-select"}
                        automationId={"attribute-select"}
                        ariaLabel={resources.modals.addRpServiceConfigurationAttribute.inputs.attribute.label}
                        label={resources.modals.addRpServiceConfigurationAttribute.inputs.attribute.label}
                        disabled={false}
                        isClearable={false}
                        isRequired={true}
                        isSearchable={true}
                        labelWidth={labelWidth}
                        onChange={handleAttributeChange}
                        options={attributeOptions}
                        selectedOption={state.selectedRpServiceConfigurationAttributeOption}
                    />
                    {state.rpServiceConfigurationAttribute && (
                        <SelectFormGroup
                            controlId={"attribute-label-select"}
                            automationId={"attribute-label-select"}
                            ariaLabel={resources.modals.addRpServiceConfigurationAttribute.inputs.label.label}
                            label={resources.modals.addRpServiceConfigurationAttribute.inputs.label.label}
                            disabled={!canModifyBranding}
                            isClearable={false}
                            isRequired={false}
                            isSearchable={true}
                            labelWidth={labelWidth}
                            onChange={handleAttributeLabelChange}
                            options={attributeLabelOptions}
                            selectedOption={state.selectedAttributeLabel}
                        />
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button id="add-rp-configuration-attribute-cancel" variant="secondary" onClick={onModalClose}>{resources.common.buttons.cancel.label}</Button>
                    <Button
                        id="add-rp-configuration-attribute-submit"
                        disabled={!state.selectedRpServiceConfigurationAttributeOption}
                        variant="primary"
                        type={ButtonTypes.SUBMIT}
                    >
                        {resources.modals.addRpServiceConfigurationAttribute.buttons.addAttribute.label}
                    </Button>
                </Modal.Footer>
            </Form>
        </DefaultModal>
    );
};